// MarketingOptIn — reusable, self-contained "keep me on your list" checkbox for
// step 2 of a program registration (or any other public EE intake form).
//
// Design contract (mirrors SmsOptIn.tsx):
//   - Single, UNCHECKED, OPTIONAL checkbox. Never required to complete the host
//     form — this block posts independently to its own endpoint and never
//     blocks the registration it sits inside.
//   - Purpose: some registrants have previously unsubscribed from all TSB
//     marketing email in HubSpot. Registering for a program doesn't undo that —
//     HubSpot stays opted-out even while EE keeps sending program emails
//     (gated on EE's own suppression list, not HubSpot's). Checking this box is
//     the contact's own, explicit re-consent, which both HubSpot's compliance
//     model and CASL require for a marketing resubscribe.
//   - Posts to POST /api/public/subscriptions/optin, which writes a confirmed
//     contact_subscriptions row (EE stays system of record for re-consent) and
//     fires a best-effort HubSpot resubscribe. If HubSpot declines the API
//     push (it can, for contacts who unsubscribed via an email link), the EE
//     side still holds valid consent evidence for engine sends.

import { useState } from 'react';
import type { CSSProperties } from 'react';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';

export type MarketingOptInSource = 'program-register' | 'registration' | 'waitlist' | 'call-rsvp' | 'magnet';

interface Props {
  email: string | null;
  firstName?: string | null;
  lastName?: string | null;
  source: MarketingOptInSource;
  sourceDetail?: string | null;
  programSlug?: string | null;
  brandId?: string | null;
  brandLabel?: string;
  style?: CSSProperties;
}

export function MarketingOptIn({
  email, firstName, lastName, source, sourceDetail, programSlug, brandId, brandLabel, style,
}: Props) {
  const [checked, setChecked] = useState(false);
  const [status, setStatus] = useState<'idle' | 'saving' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const onChange = async (next: boolean) => {
    setChecked(next);
    setError(null);
    if (!next || !email) return;
    setStatus('saving');
    try {
      const res = await fetch(`${API_BASE}/api/public/subscriptions/optin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email, firstName: firstName ?? null, lastName: lastName ?? null,
          source, sourceDetail: sourceDetail ?? null, programSlug: programSlug ?? null, brandId: brandId ?? null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        setStatus('error');
        setError('Could not save your preference. Please try again.');
        return;
      }
      setStatus('done');
    } catch {
      setStatus('error');
      setError('Network error. Please try again.');
    }
  };

  if (status === 'done') {
    return (
      <div style={{ ...cardBase, ...style }} data-marketing-optin="done">
        <span style={{ fontSize: 13, color: '#555' }}>You're back on the list ✓</span>
      </div>
    );
  }

  return (
    <div style={{ ...cardBase, ...style }} data-marketing-optin="form">
      <label style={checkRow}>
        <input
          type="checkbox"
          checked={checked}
          disabled={status === 'saving'}
          onChange={(e) => void onChange(e.target.checked)}
        />
        <span>
          <span style={optionalPrefix}>Optional — </span>
          Yes, keep me on {brandLabel ?? 'The Successful Bookkeeper'}'s email list for programs, offers, and updates.
        </span>
      </label>
      {error && <div style={{ color: '#c0392b', fontSize: 12, marginTop: 4 }}>{error}</div>}
    </div>
  );
}

const cardBase: CSSProperties = { marginTop: 12 };
const checkRow: CSSProperties = {
  display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13,
  color: '#33334d', cursor: 'pointer', lineHeight: 1.4,
};
const optionalPrefix: CSSProperties = { fontWeight: 700, color: '#0A0A2E' };
